'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { useState } from 'react';
import { CTA } from '@/components/CTA/CTA';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';

export default function MessagingSolutionsGrid({
	heading = 'Enrich Your User Experience With Multiple Bulk Messaging Solutions',
	solutions,
	CTAs = [
		{
			id: 1,
			text: 'Schedule Free Demo',
			URL: '#demo',
			target: '_self',
			variant: 'primary',
		},
		{
			id: 2,
			text: 'Contact Us',
			URL: 'tel:+91-9868629924',
			target: '_self',
			variant: 'outline',
		},
	],
	isFirst = false,
}: components['schemas']['DynamicZoneMessagingSolutionsGridComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = getSectionMotionHeading(isFirst);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

	const handleCardClick = (index: number) => {
		setSelectedIndex(selectedIndex === index ? null : index);
	};

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: {
			opacity: 0,
			y: 20,
		},
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				type: 'spring',
				stiffness: 100,
				damping: 15,
			},
		},
	};

	return (
		<section
			className={cn(
				'messaging-solutions-grid-section mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8',
			)}>
			<noscript>
				<style>{`
					.messaging-solutions-grid-section h2[style],
					.messaging-solutions-grid-section h3[style],
					.messaging-solutions-grid-section p[style],
					.messaging-solutions-grid-section div[style*='opacity'],
					.messaging-solutions-grid-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<SectionHeading
				className="mb-12 text-center text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl"
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.5 }}>
				{heading}
			</SectionHeading>

			<motion.div
				className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8"
				variants={containerVariants}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true }}>
				{solutions?.map((solution, index) => (
					<motion.div
						key={index}
						variants={itemVariants}
						className={cn(
							'group relative isolate cursor-pointer rounded-2xl border p-6 transition-all duration-300',
							selectedIndex === index
								? 'border-indigo-300 bg-indigo-50/30 shadow-md'
								: 'border-gray-100 bg-white shadow-sm hover:shadow-md',
						)}
						onMouseEnter={() => setHoveredIndex(index)}
						onMouseLeave={() => setHoveredIndex(null)}
						onClick={() => handleCardClick(index)}
						whileHover={{
							y: -5,
							transition: {
								type: 'spring',
								stiffness: 300,
								damping: 15,
							},
						}}
						whileTap={{ scale: 0.98 }}>
						{/* Animated background gradient */}
						<motion.div
							className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-indigo-50/60 to-blue-50/60"
							initial={{ opacity: 0 }}
							animate={{
								opacity:
									selectedIndex === index
										? 1
										: hoveredIndex === index
											? 0.7
											: 0,
							}}
							transition={{ duration: 0.3 }}
						/>

						{/* Icon with enhanced visuals */}
						<div className="relative mb-5">
							{solution.icon?.url && (
								<div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-blue-50">
									<motion.span
										className="text-3xl"
										animate={
											selectedIndex === index
												? {
														scale: [1, 1.1, 1],
														rotate: [
															0, -5, 5, -3, 3, 0,
														],
													}
												: hoveredIndex === index
													? {
															scale: [1, 1.1, 1],
														}
													: {}
										}
										transition={{
											duration: 0.5,
											ease: 'easeInOut',
											repeat:
												selectedIndex === index
													? Infinity
													: 0,
											repeatDelay: 2,
										}}>
										<Image
											src={solution.icon.url}
											alt={solution.icon.name || ''}
											width={15}
											height={15}
											className="h-7 w-7 object-contain"
										/>
									</motion.span>
								</div>
							)}

							{/* Decorative elements with enhanced animations */}
							<motion.div
								className="absolute -inset-1 -z-10 -rotate-3 transform rounded-2xl bg-indigo-50/70"
								animate={{
									rotate:
										selectedIndex === index
											? -6
											: hoveredIndex === index
												? -4
												: -3,
									opacity:
										selectedIndex === index
											? 1
											: hoveredIndex === index
												? 0.9
												: 0.7,
								}}
								transition={{ duration: 0.3 }}
							/>
							<motion.div
								className="absolute -inset-1 -z-20 rotate-3 transform rounded-2xl bg-blue-50/50"
								animate={{
									rotate:
										selectedIndex === index
											? 6
											: hoveredIndex === index
												? 4
												: 3,
									opacity:
										selectedIndex === index
											? 1
											: hoveredIndex === index
												? 0.9
												: 0.7,
								}}
								transition={{ duration: 0.3 }}
							/>
						</div>

						{/* Title with enhanced hover effect */}
						<motion.h3
							className={cn(
								'mb-3 text-xl font-semibold transition-colors duration-200',
								selectedIndex === index
									? 'text-indigo-700'
									: 'text-gray-900 group-hover:text-indigo-600',
							)}
							animate={
								hoveredIndex === index
									? { x: [0, 3, 0] }
									: { x: 0 }
							}
							transition={{
								duration: 0.3,
								ease: 'easeInOut',
							}}>
							{solution.title}
						</motion.h3>

						{/* Description */}
						{solution.description && (
							<div
								className={cn(
									'leading-relaxed transition-colors duration-200',
									selectedIndex === index
										? 'text-gray-700'
										: 'text-gray-600 group-hover:text-gray-700',
								)}
								dangerouslySetInnerHTML={{
									__html: solution.description,
								}}
							/>
						)}
					</motion.div>
				))}
			</motion.div>

			{/* Enhanced CTAs */}
			<motion.div
				className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ delay: 0.4 }}>
				{<CTA CTAs={CTAs} />}
			</motion.div>
		</section>
	);
}
